import { useState } from "react"
import { InstagramAccount } from "../../settings/AccountsTab"
import { ScheduledPost, PostType } from "../../../types/scheduled-post"
import { useFileUpload } from "../../../hooks/useFileUpload"
import { usePostForm } from "../../../hooks/usePostForm"
import { api } from "../../../lib/api"
import { useSelectedAccount } from "../../../contexts/AccountContext"
import PostTypeSelector from "./components/PostTypeSelector"
import MediaUploader from "./components/MediaUploader"
import CategorySelector from "./components/CategorySelector"
import AudioSection from "./components/AudioSection"
import SplitButton from "./components/SplitButton"

type Props = {
  accounts: InstagramAccount[]
  selectedAccountId: number
  onSuccess: (post: ScheduledPost) => void
  editPost?: ScheduledPost
  disabled?: boolean
  planLocked?: boolean
  onDelete?: () => void
}

const PostCreateForm = ({ accounts, selectedAccountId, onSuccess, editPost, disabled = false, planLocked = false, onDelete }: Props) => {
  const [postType, setPostType] = useState<PostType>(() => editPost?.post_type ?? "carousel")
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting]                   = useState(false)
  const [deleteError, setDeleteError]             = useState<string | null>(null)

  const selectedAccount = accounts.find(a => a.id === selectedAccountId)
  const isConnected = selectedAccount?.connected && selectedAccount?.token_status === "valid"

  // set_organization が未指定時に current_user.organizations.first へフォールバックするため、
  // 削除も他の組織スコープ操作と同じく organization_id を明示する（usePostForm と同じ逆引き）
  const { organizations } = useSelectedAccount()
  const orgId = organizations.find(o => o.accounts.some(a => a.id === selectedAccountId))?.id

  const {
    items, removedIds, isDragging, isValidatingAny, hasFileErrors, isUploadingAny, hasUploadErrors,
    carouselSizeError,
    handleFilesAdded, removeFile, handleReorder, handleDrop, handleDragOver, handleDragLeave,
  } = useFileUpload(
    postType,
    editPost?.media_attachments.map(a => ({ kind: "existing" as const, ...a })) ?? []
  )

  const {
    title, setTitle,
    caption, setCaption, captionLength, captionCounterClass,
    scheduledAt, handleScheduledAtChange, scheduledAtError,
    categoryId, setCategoryId,
    audio, setAudio,
    submitting, submitError,
    draftSubmitting, draftError,
    publishNowSubmitting, publishNowError,
    tooManyFiles, canSubmit, handleSubmit,
    canSubmitDraft, handleDraftSubmit,
    canSubmitPublishNow, handlePublishNow,
  } = usePostForm({ selectedAccountId, isConnected: !!isConnected && !disabled && !planLocked, postType, items, removedIds, isValidatingAny, hasFileErrors, isUploadingAny, hasUploadErrors, carouselSizeError, onSuccess, editPost })

  const handleDelete = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      const params = orgId != null ? `?organization_id=${orgId}` : ""
      await api.delete(`/scheduled_posts/${editPost!.id}${params}`)
      onDelete?.()
    } catch {
      setDeleteError("削除に失敗しました")
      setShowDeleteConfirm(false)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="post-create-form">

      {disabled ? (
        <div className="connection-error-banner">
          <i className="bi bi-exclamation-triangle" />
          複数のアカウントが選択されています。アカウントを1つ選択してから予約してください。
        </div>
      ) : planLocked ? (
        <div className="connection-error-banner">
          <i className="bi bi-lock-fill" />
          予約投稿はプロプラン以上でご利用いただけます。
        </div>
      ) : !isConnected && (
        <div className="connection-error-banner">
          <i className="bi bi-exclamation-triangle" />
          このアカウントはInstagramと未接続のため予約できません。設定 → アカウント管理から再連携してください。
        </div>
      )}

      <PostTypeSelector value={postType} onChange={setPostType} />

      <MediaUploader
        postType={postType}
        items={items}
        isDragging={isDragging}
        isValidatingAny={isValidatingAny}
        hasFileErrors={hasFileErrors}
        tooManyFiles={tooManyFiles}
        carouselSizeError={carouselSizeError}
        onFilesAdded={handleFilesAdded}
        onRemoveFile={removeFile}
        onReorder={handleReorder}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      />

      {/* 音源はリール限定（決定 #1）。リール以外では UI が消えるが state は usePostForm 側で保持する */}
      {postType === "reel" && (
        <AudioSection
          selectedAccountId={selectedAccountId}
          value={audio}
          onChange={setAudio}
        />
      )}

      <section className="post-create-form__section">
        <h2 className="post-create-form__section-title">テキスト</h2>
        <div className="form-group">
          <label className="form-group__label">タイトル（任意）</label>
          <input
            type="text"
            className="form-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="タイトルを入力"
            maxLength={100}
          />
          <p className="form-group__hint">このタイトルは管理用です。Instagramには投稿されません。</p>
        </div>
        {postType !== "story" && (
          <div className="form-group">
            <label className="form-group__label">キャプション</label>
            <textarea
              className="form-input"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="キャプションを入力..."
              rows={5}
              maxLength={2200}
            />
            <div className={`char-counter ${captionCounterClass}`}>{captionLength} / 2200</div>
            {captionLength > 2000 && (
              <div className="form-field-error">
                キャプションが長すぎます（残り {2200 - captionLength} 文字）
              </div>
            )}
          </div>
        )}
      </section>

      <section className="post-create-form__section">
        <h2 className="post-create-form__section-title">日時・カテゴリ</h2>
        <div className="form-group">
          <label className="form-group__label">予約日時</label>
          <input
            type="datetime-local"
            className="form-input"
            value={scheduledAt}
            onChange={e => handleScheduledAtChange(e.target.value)}
          />
          {scheduledAtError && <div className="form-field-error">{scheduledAtError}</div>}
        </div>
        <CategorySelector
          selectedAccountId={selectedAccountId}
          value={categoryId}
          onChange={setCategoryId}
        />
      </section>

      {(submitError || draftError || publishNowError || deleteError) && (
        <div className="media-errors__item" style={{ marginTop: 12 }}>
          {submitError || draftError || publishNowError || deleteError}
        </div>
      )}

      <div className="post-create-form__footer">
        <div>
          {editPost && (editPost.status === "draft" || editPost.status === "scheduled") && (
            <button
              className="ps-btn ps-btn--danger"
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <i className="bi bi-trash" /> 削除
            </button>
          )}
        </div>

        <SplitButton
          onSchedule={handleSubmit}
          onDraft={handleDraftSubmit}
          onPublishNow={handlePublishNow}
          scheduleDisabled={!canSubmit()}
          draftDisabled={!canSubmitDraft() || disabled}
          publishNowDisabled={!canSubmitPublishNow() || disabled}
          scheduleLoading={submitting}
          draftLoading={draftSubmitting}
          publishNowLoading={publishNowSubmitting}
        />
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <span className="modal__title">投稿を削除しますか？</span>
              <button className="modal__close" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div className="modal__body">
              <p style={{ color: "#4b5563" }}>この操作は取り消せません。</p>
            </div>
            <div className="modal__footer" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="ps-btn" onClick={() => setShowDeleteConfirm(false)}>キャンセル</button>
              <button className="ps-btn ps-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "削除中..." : "削除する"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default PostCreateForm
